import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Plus, Pencil, Trash2, ChevronRight } from 'lucide-react';
import { useServiceViewModel } from '../../view-model/professional/company/service-view-model';
import { optionViewModel } from '../../view-model/professional/company/option-view-model';
import OptionForm from './option-form';
import ConfirmationModal from '../ui/confirmation-modal';
import { Option } from '../../model/professional/option-model';

interface ServiceOptionsStepProps {
  serviceId: string;
}

const ServiceOptionsStep = observer((props: ServiceOptionsStepProps) => {
  const { serviceId } = props;
  const viewModel = useServiceViewModel();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingOption, setEditingOption] = useState<Option | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    optionId: string | null;
  }>({ isOpen: false, optionId: null });

  const categories = viewModel.getCategoriesByServiceId(serviceId);

  const handleAddOption = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setEditingOption(null);
    setShowForm(true);
  };

  const handleEditOption = (option: Option, categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setEditingOption(option);
    setShowForm(true);
  };

  const handleDeleteOption = async () => {
    if (deleteModal.optionId) {
      await optionViewModel.deleteOption(deleteModal.optionId);
      await optionViewModel.getOptionsByServiceId(serviceId);
      setDeleteModal({ isOpen: false, optionId: null });
    }
  };

  const handleFormSuccess = async () => {
    setShowForm(false);
    setEditingOption(null);
    setSelectedCategoryId(null);
    await optionViewModel.getOptionsByServiceId(serviceId);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingOption(null);
    setSelectedCategoryId(null);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-auto p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Options du service
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Ajoutez des options à vos catégories pour permettre la personnalisation. Cette étape est facultative.
          </p>
        </div>

        {categories.length === 0 ? (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Vous n'avez créé aucune catégorie. Revenez à l'étape précédente pour en ajouter, ou passez directement à la fin.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Formulaire d'ajout/édition si ouvert */}
            {showForm && selectedCategoryId && (
              <OptionForm
                mode={editingOption ? 'edit' : 'create'}
                categoryId={selectedCategoryId}
                option={editingOption || undefined}
                onSuccess={handleFormSuccess}
                onCancel={handleCancelForm}
              />
            )}

            {/* Liste des catégories avec leurs options */}
            {categories.map((category) => {
              const options = optionViewModel.getOptionsByCategoryId(category.id);
              const isExpanded = !showForm || selectedCategoryId === category.id;

              return (
                <div
                  key={category.id}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                >
                  {/* En-tête de catégorie */}
                  <div className="bg-gray-50 dark:bg-gray-800/50 p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {category.name}
                        </h4>
                        {category.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {category.description}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleAddOption(category.id)}
                        className="inline-flex items-center px-3 py-1.5 rounded-md bg-primary text-white hover:bg-primary-dark transition-colors text-sm"
                      >
                        <Plus size={16} className="mr-1" />
                        Ajouter une option
                      </button>
                    </div>
                  </div>

                  {/* Liste des options */}
                  {isExpanded && (
                    <div className="p-4">
                      {options.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {options.map((option) => (
                            <div
                              key={option.id}
                              className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:border-primary/50 transition-colors"
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <h5 className="font-medium text-gray-900 dark:text-white text-sm">
                                      {option.name}
                                    </h5>
                                    <span className="text-sm font-semibold text-primary ml-2">
                                      {viewModel.formatPrice(option.price)}
                                    </span>
                                  </div>
                                  {option.description && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                      {option.description}
                                    </p>
                                  )}
                                  {!option.isActive && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 mt-2">
                                      Inactive
                                    </span>
                                  )}
                                </div>
                                <div className="flex space-x-1 ml-2">
                                  <button
                                    onClick={() => handleEditOption(option, category.id)}
                                    className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-primary transition-colors"
                                    title="Modifier"
                                  >
                                    <Pencil size={14} />
                                  </button>
                                  <button
                                    onClick={() => setDeleteModal({ isOpen: true, optionId: option.id })}
                                    className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-red-500 transition-colors"
                                    title="Supprimer"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Aucune option pour cette catégorie.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Récapitulatif */}
        {categories.length > 0 && !showForm && (
          <div className="mt-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <h4 className="font-medium text-green-800 dark:text-green-200 mb-2 flex items-center">
              <ChevronRight size={16} className="mr-1" />
              Récapitulatif
            </h4>
            <div className="space-y-1 text-sm text-green-700 dark:text-green-300">
              <p>• {categories.length} catégorie{categories.length > 1 ? 's' : ''} créée{categories.length > 1 ? 's' : ''}</p>
              <p>
                • {categories.reduce((total, cat) => total + optionViewModel.getOptionsByCategoryId(cat.id).length, 0)} option
                {categories.reduce((total, cat) => total + optionViewModel.getOptionsByCategoryId(cat.id).length, 0) > 1 ? 's' : ''} ajoutée
                {categories.reduce((total, cat) => total + optionViewModel.getOptionsByCategoryId(cat.id).length, 0) > 1 ? 's' : ''}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, optionId: null })}
        onConfirm={handleDeleteOption}
        title="Supprimer l'option"
        message="Êtes-vous sûr de vouloir supprimer cette option ? Cette action est irréversible."
        type="warning"
        confirmText="Supprimer"
        cancelText="Annuler"
      />
    </div>
  );
});

export default ServiceOptionsStep;
